import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo } from "./baseApi";
import { WorkOrder } from "./workOrderApi";
import { SalesOrder } from "./salesOrderApi";
import { Endgood } from "./endgoodApi";

// Enum for PackingStatus
export enum PackingStatus {
  PACKED = 'PACKED',
  INVOICED = 'INVOICED'
}

// Enum for BoxStatus
export enum BoxStatus {
  FULL = 'FULL',
  NOT_FULL = 'NOT_FULL'
}

// BoxType interface
export interface BoxType {
  id: number;
  name: string;
  description?: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  maxQuantity?: number;
  isActive: boolean;
  cost?: number;
  createdAt: string;
  updatedAt: string;
}

// Completed Work Order Unpacked interface (matches UnpackedWorkOrderDto)
// Note: Supports both old format (id) and new format (workOrderId) for backward compatibility
export interface CompletedWorkOrderUnpacked {
  workOrderId?: number; // New format
  id?: number; // Old format (for backward compatibility)
  workOrderNumber: string;
  branchId?: number;
  completedQuantity?: number;
  totalPackedQuantity?: number;
  remainingToPack?: number;
  endgoodId?: number;
  endgoodName: string;
}

// EndgoodBoxType interface
export interface EndgoodBoxType {
  id: number;
  endgoodId: number;
  boxTypeId: number;
  minQuantity: number;
  maxQuantity: number;
  isDefault: boolean;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
  
  // Relationships
  endgood?: Endgood;
  boxType?: BoxType;
}

// PackingDetail interface
export interface PackingDetail {
  id: number;
  headerBatchId: number; // Changed from packingHeaderId to headerBatchId
  boxNumber: string;
  status: BoxStatus;
  quantity: number;
  notes?: string;
  isInvoiced?: boolean; // Indicates if the box is already invoiced
  invoiceNumber?: string; // Invoice number if already invoiced
  invoiceDate?: string; // Invoice date if already invoiced
  
  // Relationships
  packingHeader?: PackingHeader;
  boxType?: BoxType;
}

// PackingHeader interface
export interface PackingHeader {
  id: number;
  packingHeaderNo: string;
  workOrderId: number;
  salesOrderId?: number;
  quantity: number;
  rejectQuantity?: number;
  packedByWorkerId?: number;
  packedBy?: string | null;
  totalBoxes?: number | null;
  boxTypeId: number;
  batchId?: number | null;
  packedOn?: string | null;
  createdBy?: string | number;
  createdOn?: string | null;
  branchId?: number | null;
  status?: PackingStatus | null;
  updatedAt?: string | null;
  
  // Flattened fields from API response
  workOrderNumber?: string;
  salesOrderNumber?: string;
  customerId?: number | null;
  customerName?: string;
  customerCode?: string;
  endgoodId?: number | null;
  endgoodName?: string;
  endgoodSku?: string;
  boxTypeName?: string;
  branchName?: string;
  branchCode?: string;
  invoiceNumber?: string; // Invoice number if already invoiced
  invoiceDate?: string; // Invoice date if already invoiced

  // Relationships (kept for backward compatibility)
  workOrder?: WorkOrder;
  salesOrder?: SalesOrder;
  packingDetails?: PackingDetail[];
}

// API Response types
export interface PackingHeaderResponse extends ApiResponse<PackingHeader[]> {}
export interface PackingDetailResponse extends ApiResponse<PackingDetail[]> {}
export interface BoxTypeResponse extends ApiResponse<BoxType[]> {}
export interface EndgoodBoxTypeResponse extends ApiResponse<EndgoodBoxType[]> {}

// DTOs for API operations
export interface PackingHeaderCreateRequest {
  packingHeaderNo?: string;
  workOrderId: number;
  salesOrderId?: number;
  endgoodId?: number;
  quantity: number;
  rejectQuantity?: number;
  packedByWorkerId?: number; // Worker ID
  totalBoxes?: number;
  packedOn?: string;
  branchId: number;
  status?: PackingStatus;
  boxTypeId: number;

  createdBy?: number;
  createdOn?: string;
}

export interface PackingHeaderUpdateRequest extends PackingHeaderCreateRequest {
  id: number;
}

export interface PackingDetailCreateRequest {
  headerBatchId: number; // Changed from packingHeaderId to headerBatchId (use packingHeader.batchId)
  boxNumber: string;
  status?: BoxStatus;
  quantity: number;
  notes?: string;
}

export interface PackingDetailUpdateRequest extends PackingDetailCreateRequest {
  id: number;
}

export interface BoxTypeCreateRequest {
  name: string;
  description?: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  maxQuantity?: number;
  isActive?: boolean;
  cost?: number;
}

export interface BoxTypeUpdateRequest extends BoxTypeCreateRequest {
  id: number;
}

export interface EndgoodBoxTypeCreateRequest {
  endgoodId: number;
  boxTypeId: number;
  minQuantity: number;
  maxQuantity: number;
  isDefault?: boolean;
  isActive?: boolean;
  priority?: number;
}

export interface EndgoodBoxTypeUpdateRequest extends EndgoodBoxTypeCreateRequest {
  id: number;
}

// Filter interfaces
export interface PackingHeaderFilters {
  status?: PackingStatus;
  workOrderId?: number;
  salesOrderId?: number;
  branchId?: number;
  batchId?: number;
  packedBy?: string;
  startDate?: string;
  endDate?: string;
}

export interface PackingDetailFilters {
  packingHeaderId?: number;
  status?: BoxStatus;
  boxTypeId?: number;
  startDate?: string;
  endDate?: string;
}

export interface BoxTypeFilters {
  isActive?: boolean;
  name?: string;
}

export interface EndgoodBoxTypeFilters {
  endgoodId?: number;
  boxTypeId?: number;
  isActive?: boolean;
  isDefault?: boolean;
}

// Statistics interfaces
export interface PackingHeaderStats {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  totalQuantity: number;
  totalBoxes: number;
}

export interface PackingDetailStats {
  total: number;
  full: number;
  notFull: number;
  totalQuantity: number;
  totalBoxes: number;
}

// Main Packing API Service
class PackingApiService extends BaseApiService {

  // ==================== PACKING HEADERS ====================

  // Get all packing headers with pagination and filters
  async getPackingHeaders(
    page: number = 1, 
    pageSize: number = 10,
    filters?: PackingHeaderFilters
  ): Promise<ApiResponse<PackingHeader[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<PackingHeader[]>>(
        `${API_ENDPOINTS.PACKING_ENTRIES.LIST}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('PackingApiService: Error fetching packing headers:', error);
      toast.error('Failed to fetch packing headers');
      throw error;
    }
  }

  // Get packing header by ID
  async getPackingHeaderById(id: string): Promise<ApiResponse<PackingHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<PackingHeader>>(
        API_ENDPOINTS.PACKING_ENTRIES.DETAIL(id)
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch packing header details');
      throw error;
    }
  }

  // Get packing headers by batch ID
  async getPackingHeadersByBatchId(
    batchId: string | number,
    branchId?: number
  ): Promise<ApiResponse<PackingHeader[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<PackingHeader[]>>(
        API_ENDPOINTS.PACKING_ENTRIES.BY_BATCH(batchId, branchId)
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch packing headers by batch');
      throw error;
    }
  }

  // Create new packing header
  async createPackingHeader(headerData: PackingHeaderCreateRequest): Promise<ApiResponse<PackingHeader>> {
    try {
      // console.log('📡 PackingApiService.createPackingHeader - Input headerData:', headerData);
      // console.log('📡 PackingApiService.createPackingHeader - salesOrderId in input:', headerData.salesOrderId);
      // console.log('📡 PackingApiService.createPackingHeader - salesOrderId type:', typeof headerData.salesOrderId);
      
      // Build request data explicitly to ensure salesOrderId is included if it exists
      // Backend will generate packingHeaderNo automatically
      const requestData: PackingHeaderCreateRequest = {
        workOrderId: headerData.workOrderId,
        salesOrderId: headerData.salesOrderId, // Include even if undefined (will be omitted by JSON.stringify if undefined)
        endgoodId: headerData.endgoodId,
        quantity: headerData.quantity,
        rejectQuantity: headerData.rejectQuantity,
        packedByWorkerId: headerData.packedByWorkerId,
        totalBoxes: headerData.totalBoxes,
        packedOn: headerData.packedOn,
        branchId: headerData.branchId,
        status: headerData.status,
        boxTypeId: headerData.boxTypeId,
        createdBy: headerData.createdBy,
        createdOn: headerData.createdOn
      };
      
      // console.log('📡 PackingApiService.createPackingHeader - Request data:', requestData);
      // console.log('📡 PackingApiService.createPackingHeader - salesOrderId in request:', requestData.salesOrderId);
      // console.log('📡 PackingApiService.createPackingHeader - Request body JSON:', JSON.stringify(requestData, null, 2));
      
      const response = await this.makeRequest<ApiResponse<PackingHeader>>(
        API_ENDPOINTS.PACKING_ENTRIES.CREATE, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(requestData),
        }
      );
      toast.success('Packing header created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create packing header');
      throw error;
    }
  }

  // Create multiple packing headers in bulk (all share the same batch ID)
  async createBulkPackingHeaders(headersData: PackingHeaderCreateRequest[]): Promise<ApiResponse<PackingHeader[]>> {
    try {
      // Build request data for each header
      // Backend will generate packingHeaderNo automatically
      const requestData = headersData.map(headerData => {
        // Ensure rejectQuantity is properly set (handle undefined, null, or ensure it's a number)
        const rejectQty = headerData.rejectQuantity !== undefined && headerData.rejectQuantity !== null
          ? (typeof headerData.rejectQuantity === 'string' ? parseInt(headerData.rejectQuantity) || 0 : headerData.rejectQuantity)
          : 0;
        
        return {
          workOrderId: headerData.workOrderId,
          salesOrderId: headerData.salesOrderId,
          endgoodId: headerData.endgoodId,
          quantity: headerData.quantity,
          rejectQuantity: rejectQty,
          packedByWorkerId: headerData.packedByWorkerId,
          totalBoxes: headerData.totalBoxes,
          packedOn: headerData.packedOn,
          branchId: headerData.branchId,
          status: headerData.status,
          boxTypeId: headerData.boxTypeId,
          createdBy: headerData.createdBy,
          createdOn: headerData.createdOn
        };
      });
      
      const response = await this.makeRequest<ApiResponse<PackingHeader[]>>(
        API_ENDPOINTS.PACKING_ENTRIES.CREATE_BULK, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(requestData),
        }
      );
      toast.success(`Successfully created ${headersData.length} packing header(s) with shared batch ID!`);
      return response;
    } catch (error) {
      toast.error('Failed to create bulk packing headers');
      throw error;
    }
  }

  // Update packing header
  async updatePackingHeader(id: string, headerData: PackingHeaderUpdateRequest): Promise<ApiResponse<PackingHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<PackingHeader>>(
        API_ENDPOINTS.PACKING_ENTRIES.UPDATE(id), 
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(headerData),
        }
      );
      toast.success('Packing header updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update packing header');
      throw error;
    }
  }

  // Delete packing header
  async deletePackingHeader(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PACKING_ENTRIES.DELETE(id), 
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Packing header deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete packing header');
      throw error;
    }
  }

  async deletePackingHeadersByBatchId(batchId: number): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PACKING_ENTRIES.DELETE_BATCH(batchId),
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Packing headers deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete packing headers');
      throw error;
    }
  }

  // Search packing headers
  async searchPackingHeaders(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: PackingHeaderFilters
  ): Promise<ApiResponse<PackingHeader[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (filters?.branchId) {
        params.append('branchId', filters.branchId.toString());
      }

      if (filters?.status) {
        params.append('status', filters.status.toString());
      }

      if (query && query.trim()) {
        params.append('search', query.trim());
      }

      const response = await this.makeRequest<ApiResponse<PackingHeader[]>>(
        `${API_ENDPOINTS.PACKING_ENTRIES.LIST}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to search packing headers');
      throw error;
    }
  }

  // Get packing headers by work order
  async getPackingHeadersByWorkOrder(
    workOrderId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<PackingHeader[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<PackingHeader[]>>(
        `${API_ENDPOINTS.PACKING_ENTRIES.BY_WORK_ORDER(workOrderId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch work order packing headers');
      throw error;
    }
  }

  // Get packing headers by status
  async getPackingHeadersByStatus(
    status: PackingStatus, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<PackingHeader[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<PackingHeader[]>>(
        `${API_ENDPOINTS.PACKING_ENTRIES.BY_STATUS(status)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error(`Failed to fetch ${status.toLowerCase()} packing headers`);
      throw error;
    }
  }

  // Update packing header status
  async updatePackingHeaderStatus(id: string, status: PackingStatus): Promise<ApiResponse<PackingHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<PackingHeader>>(
        API_ENDPOINTS.PACKING_ENTRIES.UPDATE(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ status }),
        }
      );
      toast.success(`Packing header status updated to ${status.toLowerCase()}`);
      return response;
    } catch (error) {
      toast.error('Failed to update packing header status');
      throw error;
    }
  }

  // Get packing header statistics
  async getPackingHeaderStats(filters?: PackingHeaderFilters): Promise<ApiResponse<PackingHeaderStats>> {
    try {
      let endpoint = API_ENDPOINTS.PACKING_ENTRIES.GET_STATS;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      }

      const response = await this.makeRequest<ApiResponse<PackingHeaderStats>>(endpoint);
      return response;
    } catch (error) {
      toast.error('Failed to fetch packing header statistics');
      throw error;
    }
  }

  // ==================== PACKING DETAILS ====================

  // Get packing details by header ID
  async getPackingDetailsByHeader(
    packingHeaderId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<PackingDetail[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<PackingDetail[]>>(
        API_ENDPOINTS.PACKING_ENTRIES.BY_HEADER_ID(packingHeaderId)
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch packing details');
      throw error;
    }
  }

  // Get packing details (boxes) by batch ID / batch code
  async getPackingDetailsByBatchId(
    batchId: string | number
  ): Promise<ApiResponse<PackingDetail[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<PackingDetail[]>>(
        API_ENDPOINTS.PACKING_ENTRIES.BY_BATCH_DETAILS(batchId)
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch packing details by batch');
      throw error;
    }
  }

  // Create packing detail
  async createPackingDetail(detailData: PackingDetailCreateRequest): Promise<ApiResponse<PackingDetail>> {
    try {
      const response = await this.makeRequest<ApiResponse<PackingDetail>>(
        `${API_ENDPOINTS.PACKING_ENTRIES.CREATE}/details`, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(detailData),
        }
      );
      toast.success('Packing detail created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create packing detail');
      throw error;
    }
  }

  // Update packing detail
  async updatePackingDetail(id: string, detailData: PackingDetailUpdateRequest): Promise<ApiResponse<PackingDetail>> {
    try {
      const response = await this.makeRequest<ApiResponse<PackingDetail>>(
        API_ENDPOINTS.PACKING_ENTRIES.UPDATE_PACKING_DETAIL(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(detailData),
        }
      );
      toast.success('Packing detail updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update packing detail');
      throw error;
    }
  }

  // Delete packing detail
  async deletePackingDetail(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        `${API_ENDPOINTS.PACKING_ENTRIES.DELETE(id)}/details`, 
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Packing detail deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete packing detail');
      throw error;
    }
  }

  // Mark packing details as invoiced
  async markPackingDetailsAsInvoiced(
    packingHeaderIds: number[],
    invoiceNumber?: string,
    invoiceDate?: string
  ): Promise<ApiResponse<number>> {
    try {
      const requestBody: {
        packingHeaderIds: number[];
        invoiceNumber?: string;
        invoiceDate?: string;
      } = {
        packingHeaderIds,
      };

      if (invoiceNumber) {
        requestBody.invoiceNumber = invoiceNumber;
      }

      if (invoiceDate) {
        requestBody.invoiceDate = invoiceDate;
      }

      const response = await this.makeRequest<ApiResponse<number>>(
        API_ENDPOINTS.PACKING_ENTRIES.MARK_AS_INVOICED,
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(requestBody),
        }
      );
      toast.success(response.message || `Successfully marked ${response.data} packing details as invoiced`);
      return response;
    } catch (error) {
      toast.error('Failed to mark packing details as invoiced');
      throw error;
    }
  }

  // Mark packing details as invoiced by box numbers
  async markPackingDetailsAsInvoicedByBoxNumbers(
    boxNumbers: string[],
    invoiceNumber?: string,
    invoiceDate?: string
  ): Promise<ApiResponse<number>> {
    try {
      const requestBody: {
        boxNumbers: string[];
        invoiceNumber?: string;
        invoiceDate?: string;
      } = {
        boxNumbers,
      };

      if (invoiceNumber) {
        requestBody.invoiceNumber = invoiceNumber;
      }

      if (invoiceDate) {
        requestBody.invoiceDate = invoiceDate;
      }

      const response = await this.makeRequest<ApiResponse<number>>(
        API_ENDPOINTS.PACKING_ENTRIES.MARK_AS_INVOICED_BY_BOX_NUMBERS,
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(requestBody),
        }
      );
      toast.success(response.message || `Successfully marked ${response.data} packing details as invoiced`);
      return response;
    } catch (error) {
      toast.error('Failed to mark packing details as invoiced');
      throw error;
    }
  }

  // ==================== BOX TYPES ====================

  // Get all box types
  async getBoxTypes(filters?: BoxTypeFilters): Promise<ApiResponse<BoxType[]>> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<BoxType[]>>(
        API_ENDPOINTS.PACKING_ENTRIES.BOX_TYPES
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch box types');
      throw error;
    }
  }

  // Get box type by ID
  async getBoxTypeById(id: string): Promise<ApiResponse<BoxType>> {
    try {
      const response = await this.makeRequest<ApiResponse<BoxType>>(
        API_ENDPOINTS.PACKING_ENTRIES.BOX_TYPE_BY_ID(id)
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch box type details');
      throw error;
    }
  }

  // Create box type
  async createBoxType(boxTypeData: BoxTypeCreateRequest): Promise<ApiResponse<BoxType>> {
    try {
      const response = await this.makeRequest<ApiResponse<BoxType>>(
        API_ENDPOINTS.PACKING_ENTRIES.CREATE_BOX_TYPE, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(boxTypeData),
        }
      );
      toast.success('Box type created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create box type');
      throw error;
    }
  }

  // Update box type
  async updateBoxType(id: string, boxTypeData: BoxTypeUpdateRequest): Promise<ApiResponse<BoxType>> {
    try {
      const response = await this.makeRequest<ApiResponse<BoxType>>(
        API_ENDPOINTS.PACKING_ENTRIES.UPDATE_BOX_TYPE(id), 
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(boxTypeData),
        }
      );
      toast.success('Box type updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update box type');
      throw error;
    }
  }

  // Delete box type
  async deleteBoxType(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PACKING_ENTRIES.DELETE_BOX_TYPE(id), 
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Box type deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete box type');
      throw error;
    }
  }

  // ==================== ENDGOOD BOX TYPES ====================

  // Get endgood box types
  async getEndgoodBoxTypes(filters?: EndgoodBoxTypeFilters): Promise<ApiResponse<EndgoodBoxType[]>> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const query = params.toString();
      const url = `${API_ENDPOINTS.PACKING_ENTRIES.ENDGOOD_BOX_TYPES}${query ? `?${query}` : ''}`;

      const response = await this.makeRequest<ApiResponse<EndgoodBoxType[]>>(url);
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch endgood box types');
      throw error;
    }
  }

  // Get endgood box types by endgood ID
  async getEndgoodBoxTypesByEndgood(endgoodId: string): Promise<ApiResponse<EndgoodBoxType[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodBoxType[]>>(
        API_ENDPOINTS.PACKING_ENTRIES.GET_ENDOOD_BOX_TYPES_BY_ENDOOD_ID(endgoodId)
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch endgood box types');
      throw error;
    }
  }

  // Get default box type for endgood
  async getDefaultBoxTypeForEndgood(endgoodId: number): Promise<ApiResponse<BoxType | null>> {
    try {
      const response = await this.getEndgoodBoxTypesByEndgood(endgoodId.toString());
      
      if (response.success && response.data.length > 0) {
        // Find the default box type
        const defaultBoxType = response.data.find(ebt => ebt.isDefault);
        if (defaultBoxType) {
          // Get the box type details
          const boxTypeResponse = await this.getBoxTypeById(defaultBoxType.boxTypeId.toString());
          return {
            success: true,
            data: boxTypeResponse.data,
            message: 'Default box type found'
          };
        }
      }
      
      return {
        success: true,
        data: null,
        message: 'No default box type found'
      };
    } catch (error) {
      // console.error('PackingApiService: Error fetching default box type:', error);
      toast.error('Failed to fetch default box type');
      throw error;
    }
  }

  // Create endgood box type
  async createEndgoodBoxType(endgoodBoxTypeData: EndgoodBoxTypeCreateRequest): Promise<ApiResponse<EndgoodBoxType>> {
    try {
            const response = await this.makeRequest<ApiResponse<EndgoodBoxType>>(
        API_ENDPOINTS.PACKING_ENTRIES.CREATE_ENDOOD_BOX_TYPE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(endgoodBoxTypeData),
        }
      );
      toast.success('Endgood box type created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create endgood box type');
      throw error;
    }
  }

  // Update endgood box type
  async updateEndgoodBoxType(id: string, endgoodBoxTypeData: EndgoodBoxTypeUpdateRequest): Promise<ApiResponse<EndgoodBoxType>> {
    try {
            const response = await this.makeRequest<ApiResponse<EndgoodBoxType>>(
        API_ENDPOINTS.PACKING_ENTRIES.UPDATE_ENDOOD_BOX_TYPE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(endgoodBoxTypeData),
        }
      );
      toast.success('Endgood box type updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update endgood box type');
      throw error;
    }
  }

  // Delete endgood box type
  async deleteEndgoodBoxType(id: string): Promise<ApiResponse<void>> {
    try {
            const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PACKING_ENTRIES.DELETE_ENDOOD_BOX_TYPE(id),
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Endgood box type deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete endgood box type');
      throw error;
    }
  }

  // ==================== ENDGOODS WITH PACKED BOXES ====================

  // Get endgoods that have available packed boxes
  async getEndgoodsWithAvailableBoxes(): Promise<ApiResponse<Endgood[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<Endgood[]>>(
        API_ENDPOINTS.PACKING_ENTRIES.ENDGOODS_WITH_AVAILABLE_BOXES,
        { method: 'GET' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching endgoods with available boxes:', error);
      toast.error('Failed to fetch endgoods with packed boxes');
      throw error;
    }
  }

  // Get available boxes for a specific endgood (requires branchId; call when user selects endgood)
  async getAvailableBoxesForEndgood(endgoodId: string, branchId: number): Promise<ApiResponse<PackingDetail[]>> {
    try {
      const params = new URLSearchParams({ branchId: branchId.toString() });
      const url = `${API_ENDPOINTS.PACKING_ENTRIES.ENDGOOD_AVAILABLE_BOXES(endgoodId)}?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<PackingDetail[]>>(url, { method: 'GET' });
      return response;
    } catch (error) {
      toast.error('Failed to fetch available boxes for endgood');
      throw error;
    }
  }

  // Get available boxes for a specific endgood with optional invoiceDetailId (e.g. for edit mode)
  async getAvailableBoxesForEndgoodWithInvoiceDetail(endgoodId: string, invoiceDetailId: number, branchId: number): Promise<ApiResponse<PackingDetail[]>> {
    try {
      const params = new URLSearchParams({ branchId: branchId.toString() });
      if (invoiceDetailId) {
        params.append('invoiceDetailId', invoiceDetailId.toString());
      }
      const url = `${API_ENDPOINTS.PACKING_ENTRIES.ENDGOOD_AVAILABLE_BOXES(endgoodId)}?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<PackingDetail[]>>(url, { method: 'GET' });
      return response;
    } catch (error) {
      toast.error('Failed to fetch available boxes for endgood');
      throw error;
    }
  }

  /**
   * Get boxes for endgood: when invoiceDetailId is provided (edit invoice), returns available boxes
   * plus boxes linked to that invoice detail. When null, returns only available boxes.
   */
  async getBoxesForEndgood(endgoodId: string, branchId: number, invoiceDetailId?: number | null): Promise<ApiResponse<PackingDetail[]>> {
    try {
      const params = new URLSearchParams({ branchId: branchId.toString() });
      if (invoiceDetailId != null && invoiceDetailId > 0) {
        params.append('invoiceDetailId', invoiceDetailId.toString());
      }
      const url = `${API_ENDPOINTS.PACKING_ENTRIES.ENDGOOD_BOXES(endgoodId)}?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<PackingDetail[]>>(url, { method: 'GET' });
      return response;
    } catch (error) {
      toast.error('Failed to fetch boxes for endgood');
      throw error;
    }
  }

  // ==================== EXPORT ====================

  // Export packing headers
  async exportPackingHeaders(filters?: PackingHeaderFilters, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    try {
      let endpoint = API_ENDPOINTS.PACKING_ENTRIES.EXPORT;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        params.append('format', format);
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      } else {
        endpoint += `?format=${format}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api/v1'}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      toast.error('Failed to export packing headers');
      throw error;
    }
  }

  // ==================== COMPLETED WORK ORDERS ====================

  // Get completed work orders that are unpacked
  async getCompletedWorkOrdersUnpacked(branchId: number): Promise<ApiResponse<CompletedWorkOrderUnpacked[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<CompletedWorkOrderUnpacked[]>>(
        API_ENDPOINTS.PACKING_ENTRIES.COMPLETED_WORK_ORDER_UNPACKED(branchId),
        { method: HTTP_METHODS.GET }
      );
      
      return response;
    } catch (error) {
      // console.error('PackingApiService: Error fetching completed work orders unpacked:', error);
      toast.error('Failed to fetch completed work orders');
      throw error;
    }
  }

  // Get unpacked summary for a specific work order
  async getUnpackedSummaryByWorkOrderId(workOrderId: number): Promise<ApiResponse<CompletedWorkOrderUnpacked>> {
    try {
      const response = await this.makeRequest<ApiResponse<CompletedWorkOrderUnpacked>>(
        API_ENDPOINTS.PACKING_ENTRIES.COMPLETED_WORK_ORDER_UNPACKED_BY_ID(workOrderId),
        { method: HTTP_METHODS.GET }
      );
      
      return response;
    } catch (error) {
      // console.error('PackingApiService: Error fetching unpacked summary for work order:', error);
      toast.error('Failed to fetch unpacked summary');
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  // Helper method to extract data from API response
  extractData<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new Error(response.message || 'API request failed');
    }
    return response.data;
  }

  // Helper method to get packing headers with extracted data
  async getPackingHeadersData(
    page: number = 1, 
    pageSize: number = 10,
    filters?: PackingHeaderFilters
  ): Promise<{ packingHeaders: PackingHeader[], pagination: PaginationInfo }> {
    const response = await this.getPackingHeaders(page, pageSize, filters);
    const packingHeaders = this.extractData(response);
    
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: packingHeaders.length,
      pageSize: pageSize
    };
    
    return {
      packingHeaders,
      pagination
    };
  }

  // Helper method to search packing headers with extracted data
  async searchPackingHeadersData(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: PackingHeaderFilters
  ): Promise<{ packingHeaders: PackingHeader[], pagination: PaginationInfo }> {
    const response = await this.searchPackingHeaders(query, page, pageSize, filters);
    const packingHeaders = this.extractData(response);
    
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: packingHeaders.length,
      pageSize: pageSize
    };
    
    return {
      packingHeaders,
      pagination
    };
  }
}

export const packingApiService = new PackingApiService();
